/**
 * Cliente do Worker.
 *
 * O servidor nao tem conta nem sessao: quem publica material assina os bytes
 * CRUS do corpo com a Ed25519 da propria identidade (ver docs/05). Aqui a
 * assinatura sai do libsodium, via `Identity.sign` — nao da WebCrypto. No
 * Worker e o contrario, e por um motivo que nao vale inverter: la o
 * libsodium-wrappers nao roda, porque instancia WASM em tempo de execucao.
 * No navegador roda, e a regra do projeto e "so libsodium onde der".
 */
import {
  toBase64Url,
  fromBase64Url,
  type Identity,
  type PreKeyBundle,
  type OneTimePreKeyPublic,
} from '@criptohomer/crypto-core'

/** Trocavel em teste e para apontar para o Miniflare local. */
export const DEFAULT_API_BASE = 'https://criptohomer-edge-staging.milcredito.workers.dev'

export const SIGNATURE_HEADER = 'x-criptohomer-signature'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly reason: string,
  ) {
    super(reason)
    this.name = 'ApiError'
  }
}

/** 429 tem tratamento proprio na UI: nao e erro do usuario nem do servidor. */
export class RateLimitedError extends ApiError {
  constructor(readonly retryAfterSeconds: number) {
    super(429, 'limite de requisicoes atingido')
    this.name = 'RateLimitedError'
  }
}

export interface RemoteBundle {
  readonly fingerprint: string
  readonly signPublicKey: string
  readonly agreePublicKey: string
  readonly signedPreKey: { keyId: number; publicKey: string; signature: string; createdAt: number }
  readonly oneTimePreKey?: { keyId: number; publicKey: string }
}

async function lerErro(res: Response): Promise<string> {
  try {
    const corpo = (await res.json()) as { error?: unknown }
    return typeof corpo.error === 'string' ? corpo.error : `HTTP ${res.status}`
  } catch {
    return `HTTP ${res.status}`
  }
}

async function conferir(res: Response): Promise<Response> {
  if (res.ok) return res
  if (res.status === 429) {
    throw new RateLimitedError(Number.parseInt(res.headers.get('retry-after') ?? '0', 10) || 0)
  }
  throw new ApiError(res.status, await lerErro(res))
}

export class WorkerApi {
  constructor(private readonly base: string = DEFAULT_API_BASE) {}

  /**
   * Monta e assina uma requisicao. O `path` vai DENTRO do corpo assinado: sem
   * isso, um corpo assinado para /v1/prekeys poderia ser reapresentado em
   * /v1/identity.
   */
  private async assinado(
    identity: Identity,
    path: string,
    payload: Record<string, unknown>,
  ): Promise<Response> {
    const corpo = JSON.stringify({
      v: 1,
      path,
      issuedAt: Math.floor(Date.now() / 1000),
      signPublicKey: toBase64Url(identity.publicIdentity.signPublicKey),
      ...payload,
    })
    const bytes = new TextEncoder().encode(corpo)
    return await fetch(`${this.base}${path}`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        [SIGNATURE_HEADER]: toBase64Url(identity.sign(bytes)),
      },
      body: corpo,
    })
  }

  async health(): Promise<{ status: string; environment: string; version: string }> {
    const res = await conferir(await fetch(`${this.base}/healthz`))
    return (await res.json()) as { status: string; environment: string; version: string }
  }

  /** Registra ou atualiza a identidade. Primeiro a registrar vence. */
  async registrarIdentidade(
    identity: Identity,
    signedPreKey: { keyId: number; publicKey: Uint8Array; signature: Uint8Array },
  ): Promise<'registrado' | 'atualizado'> {
    const res = await conferir(
      await this.assinado(identity, '/v1/identity', {
        fingerprint: identity.fingerprintText,
        agreePublicKey: toBase64Url(identity.publicIdentity.agreePublicKey),
        signedPreKey: {
          keyId: signedPreKey.keyId,
          publicKey: toBase64Url(signedPreKey.publicKey),
          signature: toBase64Url(signedPreKey.signature),
        },
      }),
    )
    const corpo = (await res.json()) as { status: 'registrado' | 'atualizado' }
    return corpo.status
  }

  async publicarPreChaves(
    identity: Identity,
    oneTimePreKeys: readonly OneTimePreKeyPublic[],
  ): Promise<number> {
    const res = await conferir(
      await this.assinado(identity, '/v1/prekeys', {
        fingerprint: identity.fingerprintText,
        oneTimePreKeys: oneTimePreKeys.map((k) => ({
          keyId: k.keyId,
          publicKey: toBase64Url(k.publicKey),
        })),
      }),
    )
    return ((await res.json()) as { accepted: number }).accepted
  }

  /** Busca o bundle de um contato. CONSOME uma one-time prekey do outro lado. */
  async buscarBundle(fingerprintText: string): Promise<PreKeyBundle> {
    const res = await conferir(
      await fetch(`${this.base}/v1/prekeys/${encodeURIComponent(fingerprintText)}`),
    )
    const b = (await res.json()) as RemoteBundle
    return {
      signPublicKey: fromBase64Url(b.signPublicKey),
      agreePublicKey: fromBase64Url(b.agreePublicKey),
      signedPreKey: {
        keyId: b.signedPreKey.keyId,
        publicKey: fromBase64Url(b.signedPreKey.publicKey),
        signature: fromBase64Url(b.signedPreKey.signature),
        createdAt: b.signedPreKey.createdAt,
      },
      ...(b.oneTimePreKey === undefined
        ? {}
        : {
            oneTimePreKey: {
              keyId: b.oneTimePreKey.keyId,
              publicKey: fromBase64Url(b.oneTimePreKey.publicKey),
            },
          }),
    }
  }

  async contarPreChaves(fingerprintText: string): Promise<number> {
    const res = await conferir(
      await fetch(`${this.base}/v1/prekeys/${encodeURIComponent(fingerprintText)}/count`),
    )
    return ((await res.json()) as { available: number }).available
  }
}
